import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { formatNumber } from 'ddc-intl';
import {
	useLabels,
	useRequestData,
	usePrefs,
	useSitemap,
	useFlags
} from 'wsm-common-data';
import { useSelector } from 'react-redux';
import { setClassNames } from 'ddc-classnames-js';
import { setNewRelicCustomAttribute } from 'ddc-new-relic';
import Form from './Form';
import SearchInput from './SearchInput';
import ButtonContainer from './ButtonContainer';
import SpeechButton from './SpeechButton';
import ClearButton from './ClearButton';
import SubmitButton from './SubmitButton';
import SearchResult from './SearchResult';
import ContactLinksList from './ContactLinksList';
import AddressCard from './AddressCard';
import FormattedTitle from './FormattedTitle';
import DepartmentHoursTabs from './DepartmentHoursTabs';
import ResultsList from './ResultsList';
import NoResults from './NoResults';
import VehicleCards from './VehicleCards';
import fetchDefaultPages from '../util/fetchDefaultPages';
import fetchPages from '../util/fetchPages';
import fetchInventory from '../util/fetchInventory';
import transformInventoryResults from '../util/transformInventoryResults';
import getRichContentResults from '../util/getRichContentResults';
import getTrackingInfoForResult from '../util/getTrackingInfoForResult';
import generateUID from '../util/generateUID';
import { g } from '../global-constants';
import filterResultsWithoutPath from '../util/filterResultsWithoutPath';

const SearchForm = ({
	inputRef = React.createRef(),
	triggerTrackingEvent = () => {},
	speechRecognitionWrapper,
	closeModal = () => {}
}) => {
	const labels = useLabels();
	const sitemap = useSitemap();
	const flags = useFlags();

	const {
		display,
		numberOfCharactersForFirstSearch,
		showHoursResults,
		showAddressResult,
		showEmailResult,
		showPhoneNumberResults,
		showPageSearchResults,
		showInventoryResults,
		showInventoryCounts,
		defaultNumberOfResultsToShow,
		showMoreLabel,
		inventorySearchResultsLabel,
		quickLinksLabel,
		addressHeadingLabel,
		addressClasses,
		addressGetDirectionsLabel,
		hoursTabsHeaderLabel,
		searchPlaceholderLabel,
		searchPlaceholderListeningLabel,
		showSearchInput,
		showVehicleCards,
		overrideAllInventoryAlias
	} = usePrefs();
	const isEmbedded = display === g.EMBEDDED;
	const { windowId, locale, widgetName, deviceType } = useRequestData();
	const isMobile = deviceType === g.MOBILE;
	const {
		searchTerm: searchParam,
		accountId,
		siteId,
		widgetSearchServiceUri,
		defaultPageAliases: aliases,
		defaultInventoryAliasesAndLabels,
		pageAlias,
		configTypes
	} = useSelector((state) => state.widgetData);

	const inventorySearchResultsAriaLabelledById = generateUID(
		`${pageAlias}_${windowId}`
	);
	const pageSearchResultsAriaLabelledById = generateUID(
		`${pageAlias}_${windowId}`
	);
	const contactLinksAriaLabelledById = generateUID(
		`${pageAlias}_${windowId}`
	);
	const addressAriaLabelledById = generateUID(`${pageAlias}_${windowId}`);
	const hoursTabsAriaLabelledById = generateUID(`${pageAlias}_${windowId}`);
	const hoursTabsId = generateUID(`${pageAlias}_${windowId}`);
	const searchInputId = generateUID(`${pageAlias}_${windowId}`);
	let indexCount = 0;
	const transformDefaultInventory = () => {
		return defaultInventoryAliasesAndLabels.map(({ alias, label }) => {
			return {
				title: labels.get(label),
				path: sitemap.get(alias)
			};
		});
	};

	const defaultInventorySearchResults = transformDefaultInventory();
	const numberOfCharactersForFirstSearchInt = parseInt(
		numberOfCharactersForFirstSearch,
		10
	);
	const defaultNumberOfResultsToShowInt = parseInt(
		defaultNumberOfResultsToShow,
		10
	);
	const labelSize = isEmbedded ? 3 : 5;
	const RESULTS_PAGE_ALIAS = 'SITE_TEXT_SEARCH_RESULTS';
	const FOUR_ZERO_FOUR_PAGE_ALIAS = '404';
	const pageNotFoundPath = sitemap.get(FOUR_ZERO_FOUR_PAGE_ALIAS);
	const resultsPageAlias =
		sitemap.get(RESULTS_PAGE_ALIAS) || pageNotFoundPath;
	const allInventoryPath =
		(overrideAllInventoryAlias
			? sitemap.get(overrideAllInventoryAlias)
			: sitemap.get('INVENTORY_LISTING_DEFAULT_AUTO_ALL')) ||
		pageNotFoundPath;
	const [activeResultIndex, setActiveResultIndex] = useState(-1);
	const activeResultPath = useRef('');
	const activeResultInfo = useRef({});
	const [searchTerm, setSearchTerm] = useState(
		isEmbedded && searchParam ? searchParam : ''
	);
	const [defaultPageSearchResults, setDefaultPageSearchResults] = useState(
		[]
	);
	const [pageSearchResults, setPageSearchResults] = useState(null); // null = use default, [] = dont show results
	const [showAllPageSearchResults, setShowAllPageSearchResults] =
		useState(false);
	const [inventorySearchResults, setInventorySearchResults] = useState({});
	const [showAllInventorySearchResults, setShowAllInventorySearchResults] =
		useState(false);
	const [richContentToDisplay, setRichContentToDisplay] = useState([]);
	const [addInfo, setAddInfo] = useState('');

	// Progressive completion states for results
	const [isSearching, setIsSearching] = useState(false);
	const [isInventoryComplete, setIsInventoryComplete] = useState(false);
	const [isPageComplete, setIsPageComplete] = useState(false);
	const [isContentComplete, setIsContentComplete] = useState(false);
	const hasCompleteResults =
		isInventoryComplete && isPageComplete && isContentComplete;

	// eslint-disable-next-line no-use-before-define
	let inventoryResults = renderInventoryOrPageResults(g.INVENTORY);
	// eslint-disable-next-line no-use-before-define
	let pageResults = renderInventoryOrPageResults(g.PAGE);

	// Conditions for determining "no results" notifications
	const hasVehicleResults = !!(
		inventorySearchResults &&
		inventorySearchResults.vehicles &&
		inventorySearchResults.vehicles.length !== 0
	);
	const hasNoInventoryResults = inventoryResults === '' && !hasVehicleResults;
	const hasNoResults = !!(
		searchTerm &&
		hasNoInventoryResults &&
		pageResults === '' &&
		richContentToDisplay.length === 0
	);

	const searchTermIsShort =
		!searchTerm || searchTerm.length < numberOfCharactersForFirstSearchInt;

	// When the query returns no results or when the input has been cleared via the Backspace key,
	// display the default results
	if (hasNoResults || searchTermIsShort) {
		indexCount = 0;
		// eslint-disable-next-line no-use-before-define
		inventoryResults = renderInventoryOrPageResults(g.INVENTORY, true);
		// eslint-disable-next-line no-use-before-define
		pageResults = renderInventoryOrPageResults(g.PAGE, true);
	}

	const onArrowKey = (direction) => {
		let index = activeResultIndex;
		switch (direction) {
			case 'up':
				index -= 1;
				if (index < 0) index = indexCount;
				setActiveResultIndex(index);
				break;
			case 'down':
				index += 1;
				if (index > indexCount) index = 0;
				setActiveResultIndex(index);
				break;
			default:
				break;
		}
	};

	const onSubmit = (event) => {
		event.preventDefault();
		setNewRelicCustomAttribute('wsSiteTextSearch-onSubmit', 'true');
		// If a single vehicle is returned (as a VehicleCard),
		// go staight to the VDP without stopping at the search results page.
		if (
			showVehicleCards === 'true' &&
			inventorySearchResults &&
			inventorySearchResults.vehicles &&
			inventorySearchResults.vehicles.length === 1 &&
			inventorySearchResults.vehicles[0].path
		) {
			triggerTrackingEvent({
				action: 'clicked',
				element: 'vin stock',
				result: 'navigated to VDP'
			});
			const { path: singleVehiclePath } =
				inventorySearchResults.vehicles[0];
			setNewRelicCustomAttribute(
				'wsSiteTextSearch-onSubmitSingleVehicleCard',
				'true'
			);
			window.location.href = singleVehiclePath || null;
			// If an Inventory or Quick Links result is selected (with the arrow keys or on hover),
			// go straight to the link without stopping at the search results page.
		} else if (activeResultIndex !== -1 && activeResultPath.current) {
			triggerTrackingEvent(activeResultInfo.current);
			setNewRelicCustomAttribute(
				'wsSiteTextSearch-onSubmitSuggestionsKeyPressed',
				'true'
			);
			window.location.href = activeResultPath.current;
		} else if (hasNoResults) {
			// No suggestions are returned, prevent navigating to the search results page
			// except for non inventory search terms like 'parts', 'service' etc
			setNewRelicCustomAttribute(
				'wsSiteTextSearch-onSubmitNoResultsNotRedirected',
				'true'
			);
			event.preventDefault();
		} else if (
			inventorySearchResults.suggestions &&
			inventorySearchResults.suggestions.length
		) {
			// Navigate to the 'all-inventory' page when suggestions are returned
			setNewRelicCustomAttribute(
				'wsSiteTextSearch-onSubmitRedirectedToAllInventory',
				'true'
			);
			window.location.href = `${allInventoryPath}?search=${encodeURIComponent(
				searchTerm
			)}`;
		} else {
			// For all other results, navigate to the search results page
			triggerTrackingEvent({
				element: 'submit button',
				result: 'text search form submitted',
				fieldValue: searchTerm
			});
			setNewRelicCustomAttribute(
				'wsSiteTextSearch-onSubmitRedirectedToSearchResults',
				'true'
			);
			window.location.href = `${resultsPageAlias}?search=${encodeURIComponent(
				searchTerm
			)}`;
		}
	};

	function onResultMouseOut(index) {
		if (activeResultIndex === index) {
			setActiveResultIndex(-1);
		}
	}

	const setRichContentResults = (query) => {
		const richContentToHide = [];
		if (showPhoneNumberResults === 'false') {
			richContentToHide.push(
				'phone-sales',
				'phone-service',
				'phone-parts',
				'phone-commercial'
			);
		}
		if (showEmailResult === 'false') {
			richContentToHide.push('email');
		}
		if (showAddressResult === 'false') {
			richContentToHide.push('address');
		}
		if (showHoursResults === 'false') {
			richContentToHide.push(
				'hours-sales',
				'hours-service',
				'hours-parts',
				'hours-commercial'
			);
		}
		setRichContentToDisplay(
			getRichContentResults(
				query,
				numberOfCharactersForFirstSearchInt,
				richContentToHide,
				locale
			)
		);
		setIsContentComplete(true);
	};

	function showAllInventorySearchResultsToggle() {
		triggerTrackingEvent({
			element: 'show more inventory autocomplete results',
			elementCTA: labels.get(showMoreLabel),
			result: 'inventory autocomplete results expanded'
		});
		setShowAllInventorySearchResults(true);
	}

	function showAllPageSearchResultsToggle() {
		triggerTrackingEvent({
			element: 'show more quick links autocomplete results',
			elementCTA: labels.get(showMoreLabel),
			result: 'quick links autocomplete results expanded'
		});
		setShowAllPageSearchResults(true);
	}

	const fetchAddInfo = useCallback(async () => {
		if (typeof window !== 'undefined' && addInfo === '') {
			// this code eventually used for wsm-contact-transformer which looks for 'iPhone'
			// in userAgent so doing a tricky thing so we do not have to pass whole userAgent

			const userAgent = window.navigator.userAgent.includes('iPhone')
				? 'iPhone'
				: 'not-apple-mobile-device';

			const fetchedAddInfo = await fetch(
				`${widgetSearchServiceUri}/addData/${accountId}/${userAgent}/${siteId}/${locale}/${widgetName}`
			);

			setAddInfo(await fetchedAddInfo.json());
		}
	}, [
		accountId,
		addInfo,
		locale,
		siteId,
		widgetName,
		widgetSearchServiceUri
	]);

	const getPageResults = async (query) => {
		if (showPageSearchResults === 'true' && typeof window !== 'undefined') {
			const pageSearchResponse = await fetchPages({
				searchTerm: query,
				siteId,
				locale,
				widgetSearchServiceUri
			});

			setPageSearchResults(pageSearchResponse);
			setShowAllPageSearchResults(false);
			setIsPageComplete(true);
		}
	};

	const getInventoryResults = async (query) => {
		const inventoryFetchTimeout =
			flags['ws-site-text-search-inventory-client-timeout'];
		if (showInventoryResults === 'true') {
			const inventorySearchResponse = await fetchInventory({
				searchTerm: query,
				siteId,
				locale,
				widgetSearchServiceUri,
				configTypes,
				resultsPageAlias,
				inventoryFetchTimeout
			});
			if (inventorySearchResponse) {
				const { suggestions, searchTerm: responseSearchTerm } =
					inventorySearchResponse;
				if (responseSearchTerm === query.replace('+', ' ')) {
					let transformedSuggestions = [];
					if (suggestions && suggestions.length) {
						transformedSuggestions = transformInventoryResults(
							suggestions,
							query,
							{
								allInventoryPath,
								newInventoryPath: sitemap.get(
									'INVENTORY_LISTING_DEFAULT_AUTO_NEW'
								),
								usedInventoryPath: sitemap.get(
									'INVENTORY_LISTING_DEFAULT_AUTO_USED'
								)
							}
						);
					}
					setInventorySearchResults({
						suggestions: transformedSuggestions,
						vehicles: inventorySearchResponse.vehicles
					});
					setIsInventoryComplete(true);
				} else {
					setInventorySearchResults({
						suggestions: [],
						vehicles: []
					});
					setIsInventoryComplete(true);
				}
			}
		}
	};

	const getDefaultPageResults = useCallback(async () => {
		if (typeof window !== 'undefined') {
			const defaultPageSearchResonse = await fetchDefaultPages({
				aliases,
				siteId,
				locale,
				widgetSearchServiceUri,
				host: window.location.host
			});

			setDefaultPageSearchResults(defaultPageSearchResonse);
		}
	}, [aliases, locale, siteId, widgetSearchServiceUri]);

	const debounce =
		typeof window !== 'undefined' && window._ && window._.debounce
			? window._.debounce
			: function debounce(action, wait) {
					let timeoutId;
					return function debouncer() {
						clearTimeout(timeoutId);
						timeoutId = setTimeout(action, wait);
					};
			  };
	const debouncedFetch = useCallback(
		debounce((query) => {
			getInventoryResults(query);
			getPageResults(query);
			setRichContentResults(query);
		}, 250),
		[]
	);

	const onInput = (userInput) => {
		const leadingSpaces = /^\s+/;
		const newSearchTerm = userInput.replace(leadingSpaces, '');
		setSearchTerm(newSearchTerm);
	};

	const onClear = () => setSearchTerm('');

	speechRecognitionWrapper.onResult((transript) => onInput(transript));

	useEffect(() => {
		// This effect only runs once to setup specific data for this component
		fetchAddInfo();
		getDefaultPageResults();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const trimmedSearchTerm = searchTerm.trim();
		if (
			trimmedSearchTerm &&
			trimmedSearchTerm.length >= numberOfCharactersForFirstSearchInt
		) {
			setIsInventoryComplete(false);
			setIsPageComplete(false);
			setIsContentComplete(false);
			debouncedFetch(trimmedSearchTerm);
		} else {
			// empty search results, restore defaults
			setPageSearchResults(null);
			setShowAllPageSearchResults(false);
			setInventorySearchResults({});
			setShowAllInventorySearchResults(false);
			setRichContentToDisplay([]);
		}

		setIsSearching(true);
		const timeoutId = setTimeout(() => {
			setIsSearching(false);
		}, 1000);
		return () => clearTimeout(timeoutId);
	}, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

	function renderInventoryOrPageResults(
		resultsListType = g.INVENTORY,
		useDefaults = false
	) {
		const isInventory = resultsListType === g.INVENTORY;
		const showSearchResults = isInventory
			? showInventoryResults
			: showPageSearchResults;
		const defaultSearchResults = isInventory
			? defaultInventorySearchResults
			: defaultPageSearchResults;
		const showAllSearchResults = isInventory
			? showAllInventorySearchResults
			: showAllPageSearchResults;
		const resultsListLabel = isInventory
			? inventorySearchResultsLabel
			: quickLinksLabel;
		const resultsListAriaLabelledById = isInventory
			? inventorySearchResultsAriaLabelledById
			: pageSearchResultsAriaLabelledById;
		const showMoreToggle = isInventory
			? showAllInventorySearchResultsToggle
			: showAllPageSearchResultsToggle;
		const { suggestions } = inventorySearchResults;

		let searchResults = isInventory ? suggestions : pageSearchResults;
		let showingAutocompleteResults = false;
		if (
			showSearchResults === 'false' ||
			(!useDefaults && searchResults && !searchResults.length)
		) {
			searchResults = [];
		} else if (useDefaults || !searchResults) {
			searchResults = defaultSearchResults || [];
		} else {
			showingAutocompleteResults = true;
		}
		searchResults = filterResultsWithoutPath(searchResults);
		const displayedSearchResults = searchResults
			.slice(
				0,
				showAllSearchResults
					? searchResults.length
					: defaultNumberOfResultsToShowInt
			)
			.map((result) => {
				indexCount += 1;
				return { ...result, index: indexCount - 1 };
			});
		if (displayedSearchResults.length) {
			const showMore =
				searchResults.length > defaultNumberOfResultsToShowInt &&
				!showAllSearchResults;
			return (
				<ResultsList
					label={labels.get(resultsListLabel)}
					labelSize={labelSize}
					ariaLabelledById={resultsListAriaLabelledById}
					showMoreText={showMore ? labels.get(showMoreLabel) : ''}
					showMoreHandler={showMoreToggle}
				>
					{displayedSearchResults.map(
						({ path, title, index, count, facets }) => {
							const trackingInfo = getTrackingInfoForResult(
								isInventory ? g.INVENTORY : 'quick link',
								!showingAutocompleteResults,
								title,
								facets,
								searchTerm
							);
							const onClick = () => {
								setNewRelicCustomAttribute(
									'wsSiteTextSearch-onSubmit',
									'true'
								);
								setNewRelicCustomAttribute(
									'wsSiteTextSearch-onSubmitRedirectedToAllInventory',
									'true'
								);
								triggerTrackingEvent(trackingInfo);
							};
							if (activeResultIndex === index) {
								activeResultPath.current = path;
								activeResultInfo.current = trackingInfo;
							}
							return (
								<SearchResult
									path={path}
									count={
										isInventory &&
										showInventoryCounts === 'true'
											? formatNumber(count, locale)
											: null
									}
									active={activeResultIndex === index}
									onMouseOver={setActiveResultIndex.bind(
										this,
										index
									)}
									onMouseOut={onResultMouseOut.bind(
										this,
										index
									)}
									onClick={onClick}
									key={index}
								>
									<FormattedTitle searchTerm={searchTerm}>
										{title}
									</FormattedTitle>
								</SearchResult>
							);
						}
					)}
				</ResultsList>
			);
		} else {
			return '';
		}
	}

	const renderContactLinksList = () => {
		if (
			addInfo &&
			addInfo.phoneNumbers &&
			richContentToDisplay.some(
				(item) =>
					[
						'phone-sales',
						'phone-service',
						'phone-parts',
						'phone-commercial',
						'email'
					].indexOf(item) > -1
			)
		) {
			// 'phoneNumbers' and 'email' cannot be destructured above this if statement because 'addInfo' may not exist yet
			const { phoneNumbers, email = '' } = addInfo;
			return (
				<ContactLinksList
					phoneNumbers={phoneNumbers}
					email={email}
					richContentToDisplay={richContentToDisplay}
					labelSize={labelSize}
					ariaLabelledById={contactLinksAriaLabelledById}
					triggerTrackingEvent={triggerTrackingEvent}
					key="ContactLinksList"
				/>
			);
		} else {
			return '';
		}
	};

	const renderAddress = () => {
		const hideLabel = richContentToDisplay.some(
			(item) =>
				[
					'email',
					'phone-sales',
					'phone-service',
					'phone-parts',
					'phone-commercial'
				].indexOf(item) > -1
		);
		let addressJSX = '';
		if (addInfo && richContentToDisplay.includes('address')) {
			const {
				dealerInfo: { name, address1, city, state, postalCode },
				mapLink
			} = addInfo;
			addressJSX = (
				<AddressCard
					name={name}
					address1={address1}
					city={city}
					state={state}
					postalCode={postalCode}
					getDirectionsLabel={labels.get(addressGetDirectionsLabel)}
					getDirectionsUrl={mapLink}
					label={!hideLabel ? labels.get(addressHeadingLabel) : ''}
					labelSize={labelSize}
					ariaLabelledById={addressAriaLabelledById}
					addressClasses={addressClasses}
					triggerTrackingEvent={triggerTrackingEvent}
					key="AddressCard"
				/>
			);
		}
		return addressJSX;
	};

	const renderHours = () => {
		if (
			addInfo &&
			addInfo.hours &&
			richContentToDisplay.some(
				(item) =>
					[
						'hours-sales',
						'hours-service',
						'hours-parts',
						'hours-commercial'
					].indexOf(item) > -1
			)
		) {
			const { hours } = addInfo;
			const departments = richContentToDisplay
				.filter((richContentName) =>
					richContentName.startsWith('hours')
				)
				.map((richContentName) => richContentName.split('-')[1]);
			return (
				<DepartmentHoursTabs
					departments={departments}
					departmentHours={hours}
					label={labels.get(hoursTabsHeaderLabel)}
					labels={labels}
					labelSize={labelSize}
					ariaLabelledById={hoursTabsAriaLabelledById}
					triggerTrackingEvent={triggerTrackingEvent}
					key="DepartmentHoursTabs"
					tabId={hoursTabsId}
				/>
			);
		} else {
			return '';
		}
	};

	const renderRichContentResults = () => {
		const renderedRichContent = [];
		renderedRichContent.push(renderContactLinksList());
		renderedRichContent.push(renderAddress());
		renderedRichContent.push(renderHours());
		return renderedRichContent.filter(Boolean);
	};

	const richContentResults = renderRichContentResults();

	const resultsWrapperClasses = [
		'flex-basis-0',
		'flex-grow-1',
		isEmbedded && 'pb-6',
		isEmbedded && !isMobile && 'col-mw-330',
		isMobile && 'col-mw-200'
	];
	const richContentResultsWrapper =
		richContentResults.length !== 0 ? (
			<div className={setClassNames(resultsWrapperClasses)}>
				{richContentResults}
			</div>
		) : (
			richContentResults
		);
	let listResultsWrapper;
	if (richContentResults.length !== 0) {
		listResultsWrapper = (
			<div className={setClassNames(resultsWrapperClasses)}>
				<React.Fragment>
					{inventoryResults}
					{pageResults}
				</React.Fragment>
			</div>
		);
	} else {
		listResultsWrapper = (
			<React.Fragment>
				{inventoryResults.length !== 0 && (
					<div className={setClassNames(resultsWrapperClasses)}>
						{inventoryResults}
					</div>
				)}
				{pageResults.length !== 0 && (
					<div className={setClassNames(resultsWrapperClasses)}>
						{pageResults}
					</div>
				)}
			</React.Fragment>
		);
	}
	const resultsClassNames = [
		'site-text-search-results-area',
		'd-flex',
		'flex-wrap',
		isEmbedded && showSearchInput === 'true' && 'mt-6'
	];
	return (
		<div className={`${isEmbedded ? g.EMBEDDED : 'p-sm-4'} mb-4`}>
			{showSearchInput === 'true' && (
				<div
					className={`${
						!isEmbedded && isMobile
							? 'd-flex align-items-center mb-4'
							: 'mb-4'
					}`}
				>
					<Form onSubmit={onSubmit} isModal={!isEmbedded}>
						<SearchInput
							placeholder={labels.get(searchPlaceholderLabel)}
							placeholderListening={labels.get(
								searchPlaceholderListeningLabel
							)}
							onInput={onInput}
							onArrowKey={onArrowKey}
							value={searchTerm}
							inputRef={inputRef} // Needed by the parent SearchModal to focus the input when the modal is opened
							triggerTrackingEvent={triggerTrackingEvent}
							speechRecognitionWrapper={speechRecognitionWrapper}
							inputId={searchInputId}
						/>
						<ButtonContainer>
							{searchTerm.length === 0 ? (
								<span>
									{/* 
									React issue:
									without the containing spans, react was replacing the speech button
									with a duplicate submit button
								*/}
									<SpeechButton
										triggerTrackingEvent={
											triggerTrackingEvent
										}
										speechRecognitionWrapper={
											speechRecognitionWrapper
										}
										inputRef={inputRef}
									/>
								</span>
							) : (
								<ClearButton
									onClear={onClear}
									triggerTrackingEvent={triggerTrackingEvent}
								/>
							)}
							<SubmitButton />
						</ButtonContainer>
					</Form>
					{!isEmbedded && isMobile && (
						<button
							className="close-btn btn-unstyled p-4"
							onClick={() => closeModal(g.CLOSE_BUTTON)}
							aria-label={labels.get('CLOSE')}
							type="button"
						>
							<i
								className="ddc-icon ddc-icon-remove2 icon-size-2"
								aria-hidden="true"
							/>
						</button>
					)}
				</div>
			)}
			<NoResults
				hasNoResults={hasNoResults}
				hasNoInventoryResults={hasNoInventoryResults && !hasNoResults}
				searchTerm={searchTerm}
				isParamSearch={isEmbedded && searchTerm === searchParam}
				hasCompleteResults={hasCompleteResults}
				triggerTrackingEvent={triggerTrackingEvent}
				isSearching={isSearching}
			/>
			<VehicleCards
				inventorySearchResults={inventorySearchResults}
				triggerTrackingEvent={triggerTrackingEvent}
				searchTerm={searchTerm}
			/>
			<div className={setClassNames(resultsClassNames)}>
				{listResultsWrapper}
				{richContentResultsWrapper}
			</div>
		</div>
	);
};

SearchForm.propTypes = {
	inputRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.shape() })
	]),
	triggerTrackingEvent: PropTypes.func,
	speechRecognitionWrapper: PropTypes.shape({
		onResult: PropTypes.func,
		stopListening: PropTypes.func
	}),
	closeModal: PropTypes.func
};

export default SearchForm;
